import { useEffect, useRef } from 'react';

/**
 * Browser Back for in-page overlays (modals, edit forms, sub-views).
 *
 * Opening an overlay adds a history entry; the browser Back button then closes
 * the top overlay instead of leaving the page. Closing it from the UI removes
 * the entry again, so history stays in sync either way.
 *
 * App's popstate handler calls `consumeBack()` first and only navigates
 * between pages when no overlay took the Back press.
 */
const stack = [];
let ignorePops = 0;
let afterPops = [];

/**
 * Run `fn` once our own pending history.back() calls have landed (or now if
 * there are none). Page navigation uses it so a closing overlay can't undo
 * the history entry of the page being opened.
 */
export const afterPendingBack = (fn) => {
    if (ignorePops > 0) afterPops.push(fn);
    else fn();
};

export const consumeBack = () => {
    if (ignorePops > 0) {
        ignorePops -= 1;
        if (ignorePops === 0) {
            const queued = afterPops;
            afterPops = [];
            queued.forEach((fn) => fn());
        }
        return true;
    }
    const top = stack.pop();
    if (top) {
        top.onBack();
        return true;
    }
    return false;
};

/** Open overlays, top last. Used by the leave-page guard. */
export const hasOpenOverlays = () => stack.length > 0;

/**
 * Close `onBack` with the browser Back button while `active` is true.
 * Pass a stable callback or one that only closes the overlay.
 */
export const useBackHandler = (active, onBack) => {
    const callback = useRef(onBack);
    callback.current = onBack;

    useEffect(() => {
        if (!active) return undefined;
        const entry = { onBack: () => callback.current() };
        let pushed = false;
        // Deferred one tick: a mount that is immediately undone (React StrictMode,
        // or a dialog closed in the same tick) must not touch history at all.
        const timer = setTimeout(() => {
            stack.push(entry);
            window.history.pushState({ overlay: true }, '', window.location.href);
            pushed = true;
        }, 0);

        return () => {
            clearTimeout(timer);
            if (!pushed) return;
            const index = stack.indexOf(entry);
            if (index !== -1) {
                // Closed from the UI (not by Back): drop our history entry
                stack.splice(index, 1);
                ignorePops += 1;
                window.history.back();
            }
        };
    }, [active]);
};
